import { getCookie, setCookie } from './cookies';
import { getURLParam } from './urls';

/**
 * Super Cookie v1 Schema
 * Consolidates multiple attribution/UTM cookies into a single structured cookie.
 * See: https://stashinvest.atlassian.net/wiki/spaces/PRODUCTDELIVERY/pages/5416976438/What+is+a+Super+Cookie
 */
export interface SuperCookieV1 {
	v: 1;
	promoCode?: string;
	legacyPromoCode?: string;
	theme?: string;
	channel?: string;
	mixName?: string;
	prompt?: string;
	frontend_platform: 'web';
	fbclid?: string;
	_fbp?: string;
	_fbc?: string;
	gclid?: string;
	irclickid?: string;
	ttclid?: string;
	rdt_cid?: string;
	utm_campaign?: string;
	utm_source?: string;
	utm_medium?: string;
	utm_content?: string;
	utm_term?: string;
	utm_provider?: string;
}

export const SUPER_COOKIE_NAME = 'stashCookie';
const SUPER_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60; // 60 days

/**
 * Gets and parses the super cookie from the browser.
 * Validates and normalizes the cookie to ensure it conforms to SuperCookieV1 schema.
 * @returns Parsed super cookie object or null if not found/invalid
 */
export function getSuperCookie(): SuperCookieV1 | null {
	const raw = getCookie(SUPER_COOKIE_NAME);
	if (!raw) {
		return null;
	}

	try {
		// Try parsing first (most cookies are not encoded)
		// Only decode if parsing fails (some repos encode for RFC 6265 compliance)
		let parsed: SuperCookieV1;
		try {
			parsed = JSON.parse(raw) as SuperCookieV1;
		} catch {
			// If parsing fails, try decoding first (cookie might be encoded)
			try {
				const decoded = decodeURIComponent(raw);
				parsed = JSON.parse(decoded) as SuperCookieV1;
			} catch {
				console.error('Failed to parse super cookie: invalid JSON');
				return null;
			}
		}

		// Validate required fields and normalize to safe defaults
		if (typeof parsed !== 'object' || parsed === null) {
			console.warn('Super cookie is not an object');
			return null;
		}

		// Validate and normalize version
		if (parsed.v !== 1) {
			console.warn('Unknown super cookie version:', parsed.v);
			return null;
		}

		// Validate and normalize frontend_platform (required field)
		if (parsed.frontend_platform !== 'web') {
			console.warn(
				'Invalid frontend_platform in super cookie:',
				parsed.frontend_platform
			);
			parsed.frontend_platform = 'web';
		}

		return parsed;
	} catch (e) {
		console.error('Failed to parse super cookie:', e);
		return null;
	}
}

/**
 * Maximum cookie size in bytes (browser limit is ~4KB, we use 3.5KB to be safe)
 */
const MAX_COOKIE_SIZE_BYTES = 3500;

/**
 * Creates a super cookie from URL query parameters and existing cookies.
 * This consolidates multiple individual cookies into a single structured cookie.
 *
 * IMPORTANT: This function implements "last touch attribution" for UTM parameters.
 * - If ANY UTM parameter has a non-empty value in the URL, ALL existing UTMs are cleared and
 *   only the new UTMs from the URL are set. This prevents mixing UTMs from different campaigns.
 *   Note: Empty UTM parameters (e.g., ?utm_source=) are treated as absent and preserve existing attribution.
 * - If NO UTM parameters are present in the URL, existing UTMs are preserved.
 * - Click IDs (gclid, fbclid, etc.) are platform-independent: only the specific
 *   click ID type is updated when present in the URL.
 *
 * @returns SuperCookieV1 object or null if no valid data to store
 */
export function createSuperCookie(): SuperCookieV1 | null {
	// Start with existing super cookie data to preserve attribution across navigations
	const existingCookie = getSuperCookie();
	const superCookie: SuperCookieV1 = existingCookie
		? {
				...existingCookie,
				v: existingCookie.v ?? 1,
				frontend_platform: existingCookie.frontend_platform ?? 'web',
			}
		: {
				v: 1,
				frontend_platform: 'web',
			};

	// Helper to get URL param
	const getParam = (key: string): string | null => getURLParam(key);

	// Read from URL params (allowlisted parameters only)
	const utmSource = getParam('utm_source');
	const utmMedium = getParam('utm_medium');
	const utmCampaign = getParam('utm_campaign');
	const utmContent = getParam('utm_content');
	const utmTerm = getParam('utm_term');
	const utmProvider = getParam('utm_provider');
	const gclid = getParam('gclid');
	const fbclid = getParam('fbclid');
	const irclickid = getParam('irclickid') || getParam('clickid');
	const ttclid = getParam('ttclid');
	const rdt_cid = getParam('rdt_cid');
	const stashCodeV2 = getParam('stash_code_v2');
	const stashCode = getParam('stash_code');
	const theme = getParam('theme');
	const channel = getParam('channel');

	// Check if a campaign-identity UTM has a non-empty value in URL (new campaign touchpoint)
	// If so, clear ALL existing UTMs to implement proper last-touch attribution
	// This prevents mixing UTMs from different campaigns
	// Note: utm_content and utm_term are campaign details, not campaign identity — they update
	// in-place and do not trigger the wipe. Empty strings (e.g., ?utm_source=) are treated as absent.
	const hasAnyNewUtm = utmSource || utmMedium || utmCampaign;
	if (hasAnyNewUtm) {
		delete superCookie.utm_source;
		delete superCookie.utm_medium;
		delete superCookie.utm_campaign;
		delete superCookie.utm_content;
		delete superCookie.utm_term;
		delete superCookie.utm_provider;
	}

	// Set UTMs from URL (only if present)
	if (utmSource) superCookie.utm_source = utmSource;
	if (utmMedium) superCookie.utm_medium = utmMedium;
	if (utmCampaign) superCookie.utm_campaign = utmCampaign;
	if (utmContent) superCookie.utm_content = utmContent;
	if (utmTerm) superCookie.utm_term = utmTerm;
	if (utmProvider) superCookie.utm_provider = utmProvider;

	// Click IDs are platform-independent: only update the specific click ID type
	if (gclid) superCookie.gclid = gclid;
	if (fbclid) superCookie.fbclid = fbclid;
	if (irclickid) superCookie.irclickid = irclickid;
	if (ttclid) superCookie.ttclid = ttclid;
	if (rdt_cid) superCookie.rdt_cid = rdt_cid;

	// Promo codes and other params
	if (stashCodeV2) superCookie.promoCode = stashCodeV2;
	if (stashCode) superCookie.legacyPromoCode = stashCode;
	if (theme) superCookie.theme = theme;
	if (channel) superCookie.channel = channel;

	// Always update Facebook pixels from cookies (they may change)
	// Clear fields if cookies are deleted to honor "always update" behavior
	const fbp = getCookie('_fbp');
	const fbc = getCookie('_fbc');
	superCookie._fbp = fbp || undefined;
	superCookie._fbc = fbc || undefined;

	// Read promo codes from existing cookies if not already set
	if (!superCookie.promoCode) {
		const existingPromoCodeV2 = getCookie('stashPromoCodeV2');
		if (existingPromoCodeV2) superCookie.promoCode = existingPromoCodeV2;
	}
	if (!superCookie.legacyPromoCode) {
		const existingPromoCode = getCookie('stashPromoCode');
		if (existingPromoCode) superCookie.legacyPromoCode = existingPromoCode;
	}

	// Only create cookie if we have at least one meaningful value (beyond version and platform)
	const hasData =
		superCookie.promoCode ||
		superCookie.legacyPromoCode ||
		superCookie.utm_source ||
		superCookie.utm_medium ||
		superCookie.utm_campaign ||
		superCookie.utm_content ||
		superCookie.utm_term ||
		superCookie.utm_provider ||
		superCookie.gclid ||
		superCookie.fbclid ||
		superCookie.irclickid ||
		superCookie.ttclid ||
		superCookie.rdt_cid ||
		superCookie._fbp ||
		superCookie._fbc ||
		superCookie.theme ||
		superCookie.channel;

	if (!hasData) {
		return null;
	}

	// Validate cookie size before setting
	// Check ENCODED size since setSuperCookie uses encodeURIComponent
	const cookieString = JSON.stringify(superCookie);
	const encodedValue = encodeURIComponent(cookieString);
	const cookieSizeBytes = new Blob([encodedValue]).size;

	if (cookieSizeBytes > MAX_COOKIE_SIZE_BYTES) {
		console.warn(
			`Super cookie size (${cookieSizeBytes} bytes encoded) exceeds limit (${MAX_COOKIE_SIZE_BYTES} bytes). Truncating.`
		);
		// Truncate by removing optional fields, starting with least important
		delete superCookie.utm_term;
		delete superCookie.utm_content;
		const truncatedString = JSON.stringify(superCookie);
		const truncatedEncoded = encodeURIComponent(truncatedString);
		const truncatedSize = new Blob([truncatedEncoded]).size;
		if (truncatedSize > MAX_COOKIE_SIZE_BYTES) {
			console.error('Super cookie still too large after truncation, skipping');
			return null;
		}
	}

	return superCookie;
}

/**
 * Sets the super cookie in the browser.
 * Cookie name: stashCookie, Path: /, Domain: .stash.com, Expires: 60 days
 *
 * @param superCookie - The super cookie object to set
 */
export function setSuperCookie(superCookie: SuperCookieV1): void {
	try {
		const cookieString = JSON.stringify(superCookie);
		// Encode the JSON string to handle special characters (RFC 6265 compliance)
		// Characters like {, }, ", and commas in JSON need encoding for cookie values
		const encodedValue = encodeURIComponent(cookieString);
		setCookie(SUPER_COOKIE_NAME, encodedValue, {
			'max-age': SUPER_COOKIE_MAX_AGE_SECONDS,
			domain: 'stash.com', // Parent domain for subdomain sharing
		});
	} catch (error) {
		console.error('Failed to set super cookie:', error);
	}
}
